import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import {
  getInstitutionDetails,
  type InstitutionDetailsPartial,
  institutionDetailsFuturesSchema,
  institutionDetailsOnboardingSchema,
  postInstitutionDetails,
} from '@/services/api/profile';

const profileInstitutionDetailsQueryKey = (pid: UserPid) => ['profile', 'institution-details', pid];

const getIsCompleteOnboarding = async (institutionDetails: InstitutionDetailsPartial | null | undefined) => {
  try {
    await institutionDetailsOnboardingSchema.parseAsync(institutionDetails);
    return true;
  } catch {
    return false;
  }
};

const getIsCompleteFutures = async (institutionDetails: InstitutionDetailsPartial | null | undefined) => {
  try {
    await institutionDetailsFuturesSchema.parseAsync(institutionDetails);
    return true;
  } catch {
    return false;
  }
};

const profileInstitutionDetailsQueryFn = async () => {
  const institutionDetails = await getInstitutionDetails();

  return {
    ...institutionDetails,
    answers: institutionDetails?.answers ?? undefined,
    isCompleteOnboarding: await getIsCompleteOnboarding(institutionDetails?.answers),
    isCompleteFutures: await getIsCompleteFutures(institutionDetails?.answers),
  };
};

type ProfileInstitutionDetailsQueryResult = Awaited<ReturnType<typeof profileInstitutionDetailsQueryFn>>;

const profileInstitutionDetailsQueryOptions = (pid: UserPid) =>
  queryOptions({
    queryFn: profileInstitutionDetailsQueryFn,
    queryKey: profileInstitutionDetailsQueryKey(pid),
  });

const useProfileInstitutionDetails = () => {
  const { pid } = useUserPid();
  return useQuery(profileInstitutionDetailsQueryOptions(pid));
};

const useProfileInstitutionDetailsMutation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();

  return useMutation({
    mutationFn: postInstitutionDetails,
    onSuccess: async (data) => {
      const queryKey = profileInstitutionDetailsQueryKey(pid);
      const isCompleteOnboarding = await getIsCompleteOnboarding(data);
      const isCompleteFutures = await getIsCompleteFutures(data);

      if (!isCompleteFutures) {
        queryClient.setQueryData<Partial<ProfileInstitutionDetailsQueryResult>>(queryKey, {
          answers: data,
          isCompleteOnboarding,
          isCompleteFutures,
        });
        return;
      }

      await queryClient.invalidateQueries({ queryKey });
    },
  });
};

const useProfileInstitutionDetailsAnswers = () => {
  const { data, ...rest } = useProfileInstitutionDetails();

  return {
    ...rest,
    data: data?.answers,
    isCompleteOnboarding: data?.isCompleteOnboarding,
    isCompleteFutures: data?.isCompleteFutures,
  };
};

const useProfileInstitutionDetailsMeta = () => {
  const { data, ...rest } = useProfileInstitutionDetails();

  return {
    ...rest,
    data: {
      clientCategorisation: data && 'categorisation' in data ? data.categorisation : null,
      isRegulated: data && 'regulated' in data ? data.regulated : null,
    },
  };
};

export { useProfileInstitutionDetails, useProfileInstitutionDetailsAnswers, useProfileInstitutionDetailsMeta, useProfileInstitutionDetailsMutation };
